/**
 * 盘点轮次与盖章共用逻辑（桌面仓库视图 / 移动端库位浏览）
 * 支持同仓多轮（按区域/库位范围互不重叠）；开/关轮仅管理员。
 */
import { ref, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useUserStore } from '@/store/user'
import { hasPermission } from '@/utils/permission'
import { confirmDangerousOperationWithAccount } from '@/utils/dangerousOperationConfirm'
import {
  getStockTakeRounds,
  createStockTakeRound,
  closeStockTakeRound,
  getStockTakeRound,
  getStockTakeSessions,
  createStockTakeSession,
  batchStampStockTakeSessions,
  voidStockTakeSession
} from '@/api/inventoryStockTake'

export function canManageStockTakeRound(user) {
  if (!user) return false
  if (user.is_superuser || user.is_admin) return true
  return hasPermission(user, 'inventory.stock_take.round_manage')
}

export function useStockTakeRounds(getWarehouseId) {
  const userStore = useUserStore()
  const openRounds = ref([])
  const roundsLoading = ref(false)
  const roundActionLoading = ref(false)
  const stampedLocationIds = ref([])

  /** 兼容旧 UI：取最近一条 open 轮 */
  const openRound = computed(() => openRounds.value[0] || null)
  const hasOpenRound = computed(() => openRounds.value.length > 0)
  const canManageRound = computed(() => canManageStockTakeRound(userStore.user))

  function applyOpenRoundsList(list) {
    openRounds.value = Array.isArray(list) ? list : []
  }

  async function loadOpenRound() {
    const wid = Number(getWarehouseId?.())
    if (!wid) {
      openRounds.value = []
      stampedLocationIds.value = []
      return null
    }
    roundsLoading.value = true
    try {
      const res = await getStockTakeRounds({ warehouse_id: wid, per_page: 50 })
      const list = Array.isArray(res?.open_rounds)
        ? res.open_rounds
        : res?.open_round
          ? [res.open_round]
          : []
      applyOpenRoundsList(list)
      stampedLocationIds.value = []
      for (const r of openRounds.value) {
        await refreshRoundProgress(r.round_id, { mergeStamps: true })
      }
      return openRound.value
    } catch (e) {
      openRounds.value = []
      stampedLocationIds.value = []
      return null
    } finally {
      roundsLoading.value = false
    }
  }

  async function refreshRoundProgress(roundId, { mergeStamps = false } = {}) {
    const id = roundId || openRound.value?.round_id
    if (!id) {
      if (!mergeStamps) stampedLocationIds.value = []
      return
    }
    try {
      const res = await getStockTakeRound(id)
      const data = res?.data || res
      const ids = Array.isArray(data?.stamped_location_ids)
        ? data.stamped_location_ids.map(Number)
        : []
      if (mergeStamps) {
        const set = new Set(stampedLocationIds.value.map(Number))
        ids.forEach(i => set.add(i))
        stampedLocationIds.value = [...set]
      } else {
        stampedLocationIds.value = ids
      }
      openRounds.value = openRounds.value.map(r =>
        Number(r.round_id) === Number(data.round_id) ? { ...r, ...data } : r
      )
    } catch (_) {
      /* ignore */
    }
  }

  /**
   * @param {{ name?: string, notes?: string, scope_mode?: 'warehouse'|'partial', scope_location_ids?: number[] }} extra
   */
  async function startRound(extra = {}) {
    if (!canManageRound.value) {
      ElMessage.warning('仅管理员可开启盘点轮次')
      return null
    }
    const wid = Number(getWarehouseId?.())
    if (!wid) {
      ElMessage.warning('请先选择仓库')
      return null
    }
    const scope_mode = extra.scope_mode === 'partial' ? 'partial' : 'warehouse'
    const scope_location_ids =
      scope_mode === 'partial' ? (extra.scope_location_ids || []).map(Number).filter(Boolean) : []
    if (scope_mode === 'partial' && !scope_location_ids.length) {
      ElMessage.warning('请选择要盘点的区域或库位')
      return null
    }
    roundActionLoading.value = true
    try {
      const res = await createStockTakeRound({
        warehouse_location_id: wid,
        name: extra.name,
        notes: extra.notes,
        scope_mode,
        scope_location_ids
      })
      const row = res?.data || null
      if (row) {
        openRounds.value = [row, ...openRounds.value.filter(r => r.round_id !== row.round_id)]
      }
      ElMessage.success(res?.message || '已开启盘点')
      return row
    } catch (e) {
      ElMessage.error(e?.response?.data?.message || e?.message || '开启失败')
      return null
    } finally {
      roundActionLoading.value = false
    }
  }

  async function endRound(roundId) {
    if (!canManageRound.value) {
      ElMessage.warning('仅管理员可结束盘点轮次')
      return false
    }
    const targetId = roundId || openRound.value?.round_id
    if (!targetId) {
      ElMessage.warning('当前没有进行中的盘点轮次')
      return false
    }
    const target = openRounds.value.find(r => Number(r.round_id) === Number(targetId))
    const label = target?.name || `#${targetId}`
    let creds
    try {
      const expected =
        userStore.user?.username || userStore.user?.name || ''
      creds = await confirmDangerousOperationWithAccount({
        title: '结束盘点轮次',
        confirmButtonText: '继续结束',
        expectedUsername: expected,
        message: `即将结束「${label}」。结束后该范围内「已盘查」印记与行「已盘」将不再显示，历史仍可查。此操作不可撤销，请输入管理员账号与密码确认。`,
      })
    } catch {
      return false
    }
    roundActionLoading.value = true
    try {
      const res = await closeStockTakeRound(targetId, {
        confirm_username: creds.username,
        operation_password: creds.password
      })
      ElMessage.success(res?.message || '本轮已结束')
      openRounds.value = openRounds.value.filter(r => Number(r.round_id) !== Number(targetId))
      stampedLocationIds.value = []
      for (const r of openRounds.value) {
        await refreshRoundProgress(r.round_id, { mergeStamps: true })
      }
      return true
    } catch (e) {
      ElMessage.error(e?.response?.data?.message || e?.message || '结束失败')
      return false
    } finally {
      roundActionLoading.value = false
    }
  }

  async function stampLocation({ storageLocationId, warehouseLocationId, locationName }) {
    const lid = Number(storageLocationId)
    if (!lid) {
      ElMessage.warning('库位无效')
      return null
    }
    try {
      const res = await createStockTakeSession({
        storage_location_id: lid,
        warehouse_location_id: Number(warehouseLocationId) || Number(getWarehouseId?.()) || undefined,
        notes: `盘点·已盘查 · ${locationName || lid}`,
        stamp_only: true
      })
      const data = res?.data || null
      if (data?.session_id && !stampedLocationIds.value.includes(lid)) {
        stampedLocationIds.value = [...stampedLocationIds.value, lid]
      }
      ElMessage.success(res?.message || '本库位已盘查')
      return data
    } catch (e) {
      ElMessage.error(e?.response?.data?.message || e?.message || '盖章失败')
      return null
    }
  }

  /**
   * 整排/多库位批量盖章。
   * @returns {Promise<object|null>} data: { stamped, skipped, ... }
   */
  async function stampLocationsBatch({
    storageLocationIds,
    warehouseLocationId,
    notes,
    skipAlreadyStamped = true
  }) {
    const ids = (storageLocationIds || [])
      .map(Number)
      .filter(n => Number.isFinite(n) && n > 0)
    if (!ids.length) {
      ElMessage.warning('无库位可盖章')
      return null
    }
    try {
      const res = await batchStampStockTakeSessions({
        storage_location_ids: ids,
        warehouse_location_id: Number(warehouseLocationId) || Number(getWarehouseId?.()) || undefined,
        notes: notes || undefined,
        skip_already_stamped: skipAlreadyStamped
      })
      const data = res?.data || null
      const stamped = Array.isArray(data?.stamped) ? data.stamped : []
      if (stamped.length) {
        const next = new Set(stampedLocationIds.value)
        for (const s of stamped) {
          const lid = Number(s.storage_location_id)
          if (lid) next.add(lid)
        }
        stampedLocationIds.value = [...next]
      }
      ElMessage.success(res?.message || '已批量盖章')
      return data
    } catch (e) {
      ElMessage.error(e?.response?.data?.message || e?.message || '整排盖章失败')
      return null
    }
  }

  async function voidLatestStamp(sessionId) {
    if (!sessionId) return false
    try {
      await ElMessageBox.confirm('作废后本库位「已盘查」印记将消失，可重新盖章。确认？', '作废盘查印记', {
        type: 'warning',
        modalClass: 'm-stock-take-msgbox-overlay',
        customClass: 'm-stock-take-msgbox'
      })
    } catch {
      return false
    }
    try {
      const res = await voidStockTakeSession(sessionId)
      ElMessage.success(res?.message || '已作废')
      await loadOpenRound()
      return true
    } catch (e) {
      ElMessage.error(e?.response?.data?.message || e?.message || '作废失败')
      return false
    }
  }

  async function listRounds() {
    const wid = Number(getWarehouseId?.())
    if (!wid) return []
    const res = await getStockTakeRounds({ warehouse_id: wid, per_page: 50 })
    return Array.isArray(res?.data) ? res.data : []
  }

  async function listSessionsForRound(roundId) {
    if (!roundId) return []
    const res = await getStockTakeSessions({
      round_id: roundId,
      per_page: 100,
      include_voided: false
    })
    return Array.isArray(res?.data) ? res.data : []
  }

  function scopeSummary(r) {
    if (!r) return ''
    if ((r.scope_mode || 'warehouse') === 'partial') {
      const labels = r.scope_location_labels || []
      if (labels.length) return labels.slice(0, 3).join('、') + (labels.length > 3 ? '…' : '')
      const ids = r.scope_location_ids || []
      return ids.length ? `指定 ${ids.length} 个范围` : '指定范围'
    }
    return '整仓'
  }

  return {
    openRound,
    openRounds,
    hasOpenRound,
    canManageRound,
    roundsLoading,
    roundActionLoading,
    stampedLocationIds,
    loadOpenRound,
    refreshRoundProgress,
    applyOpenRoundsList,
    startRound,
    endRound,
    stampLocation,
    stampLocationsBatch,
    voidLatestStamp,
    listRounds,
    listSessionsForRound,
    scopeSummary
  }
}

export function applyOpenRoundFromLayoutResponse(res, openRoundsRefOrOpenRoundRef) {
  if (!openRoundsRefOrOpenRoundRef) return
  const list = Array.isArray(res?.open_stock_take_rounds)
    ? res.open_stock_take_rounds
    : res?.open_stock_take_round
      ? [res.open_stock_take_round]
      : []
  // 兼容旧：若传入的是 openRound ref（单对象），写第一条
  if ('value' in openRoundsRefOrOpenRoundRef) {
    const v = openRoundsRefOrOpenRoundRef.value
    if (Array.isArray(v) || v === null || v === undefined || typeof v === 'object') {
      // prefer writing array if consumer uses openRounds
      try {
        openRoundsRefOrOpenRoundRef.value = list
      } catch (_) {
        openRoundsRefOrOpenRoundRef.value = list[0] || null
      }
    }
  }
}
